import { Loading } from "../../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  MAIL_BODY_MAX,
  MAIL_SUBJECT_MAX,
  type BuildingSummary,
  type ContactLinkDto,
  type DirectoryMailDto,
  type MailRecipientOption,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { errMsg } from "./DirectoryDialogs";

/** Voor wie het venster en de lijst gelden: een bedrijf, of een persoon (met of zonder bedrijf). */
export type MailTarget = { companyId: string; personId?: undefined } | { personId: string; companyId?: undefined };

const targetQuery = (t: MailTarget) => (t.companyId ? `companyId=${t.companyId}` : `personId=${t.personId}`);

type Role = "" | "to" | "cc";

/** Vooraf ingevuld venster, bv. vanuit de planning: een afspraak met agenda-uitnodiging. */
export interface MailPreset {
  subject: string;
  body: string;
  buildingId: string;
  /** Deze ontvanger staat standaard op Aan (bv. de monteur bij het bedrijf). */
  to?: string | null;
  /** De mail krijgt de agenda-uitnodiging (.ics) van deze afspraak mee; het gebouw ligt dan vast. */
  appointmentId?: string;
}

/**
 * Venster *Mail sturen* (docs/ADRESBOEK.md, "Mailen"): ontvangers uit het bedrijf (aan of cc), onderwerp,
 * bericht, eventueel het gebouw waar het over gaat en een kopie naar jezelf. Vanuit de planning komt er een
 * agenda-uitnodiging bij (docs/PLANNING.md).
 */
export function MailDialog({
  target,
  links,
  preset,
  onClose,
}: {
  target: MailTarget;
  /** Koppelingen van het bedrijf of de persoon: die gebouwen staan bovenaan bij *Over gebouw*. */
  links: ContactLinkDto[];
  preset?: MailPreset;
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const opts = useQuery({
    queryKey: ["directory-mail-options", target.companyId ?? target.personId],
    queryFn: () =>
      api<{ mailEnabled: boolean; options: MailRecipientOption[] }>(`/directory/mail-options?${targetQuery(target)}`),
  });
  const buildings = useQuery({ queryKey: ["buildings"], queryFn: () => api<BuildingSummary[]>("/buildings") });
  const [roles, setRoles] = useState<Record<string, Role> | null>(null);
  const [subject, setSubject] = useState(preset?.subject ?? "");
  const [body, setBody] = useState(preset?.body ?? "");
  const [buildingId, setBuildingId] = useState(preset?.buildingId ?? "");
  const appointmentId = preset?.appointmentId ?? null;
  const [copyToMe, setCopyToMe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [id] = useState(() => crypto.randomUUID());

  const options = opts.data?.options ?? [];
  // Standaard: de persoon zelf, of het algemene adres van het bedrijf (anders de eerste persoon).
  const initial: Record<string, Role> = {};
  const preferred = target.personId ?? preset?.to;
  const first = (preferred && options.find((o) => o.key === preferred)) || options[0];
  if (first) initial[first.key] = "to";
  const current = roles ?? initial;
  const to = options.filter((o) => current[o.key] === "to").map((o) => o.key);
  const cc = options.filter((o) => current[o.key] === "cc").map((o) => o.key);

  const linked = new Set(links.map((l) => l.building.id));
  const buildingList = [...(buildings.data ?? [])].sort(
    (a, b) => Number(linked.has(b.id)) - Number(linked.has(a.id)) || a.name.localeCompare(b.name, "nl"),
  );

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api<DirectoryMailDto>("/directory/mails", {
        method: "POST",
        body: { id, ...target, to, cc, subject, body, buildingId: buildingId || null, appointmentId, copyToMe },
      });
      await qc.invalidateQueries({ queryKey: ["logbook"] });
      toast("Mail verstuurd");
      onClose();
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };

  const disabled = opts.data?.mailEnabled === false;
  return (
    <Modal
      title="Mail sturen"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => void send()}
            disabled={busy || disabled || !to.length || !subject.trim() || !body.trim()}
          >
            Versturen
          </button>
        </>
      }
    >
      <form onSubmit={(e) => void send(e)}>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        {disabled && (
          <div className="alert" role="status">
            Mail is voor Klantpunt nog niet ingesteld. Vraag de beheerder om de mailinstellingen.
          </div>
        )}
        <fieldset className="field dir-mail-to">
          <legend className="label">Ontvangers *</legend>
          {opts.isLoading ? (
            <Loading small />
          ) : options.length ? (
            <ul className="dir-items">
              {options.map((o) => (
                <li key={o.key}>
                  <span className="grow">
                    <strong>{o.key === "company" ? "Algemeen adres" : o.name}</strong>
                    {o.jobTitle && <span className="muted small"> · {o.jobTitle}</span>}
                    <div className="muted small dir-wrap">{o.email}</div>
                  </span>
                  <select
                    aria-label={`${o.key === "company" ? "Algemeen adres" : o.name}: aan of cc`}
                    value={current[o.key] ?? ""}
                    onChange={(e) => setRoles({ ...current, [o.key]: e.target.value as Role })}
                  >
                    <option value="">—</option>
                    <option value="to">Aan</option>
                    <option value="cc">Cc</option>
                  </select>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small">
              Er is geen mailadres bekend. Vul het mailadres van het bedrijf of een persoon in via Bewerken.
            </p>
          )}
        </fieldset>
        <label className="field">
          <span className="label">Onderwerp *</span>
          <input value={subject} maxLength={MAIL_SUBJECT_MAX} onChange={(e) => setSubject(e.target.value)} />
        </label>
        <label className="field">
          <span className="label">Bericht *</span>
          <textarea rows={8} value={body} maxLength={MAIL_BODY_MAX} onChange={(e) => setBody(e.target.value)} />
        </label>
        <label className="field">
          <span className="label">Over gebouw</span>
          <select value={buildingId} disabled={!!appointmentId} onChange={(e) => setBuildingId(e.target.value)}>
            <option value="">Geen gebouw</option>
            {buildingList.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} · {b.name}
              </option>
            ))}
          </select>
        </label>
        <label className="dir-check">
          <input type="checkbox" checked={copyToMe} onChange={(e) => setCopyToMe(e.target.checked)} /> Kopie naar mezelf
        </label>
        {appointmentId && <p className="hint">De afspraak gaat mee als agenda-uitnodiging (afspraak.ics).</p>}
        <p className="hint">
          De mail gaat uit met jouw naam en de naam van je organisatie. Antwoorden komen in het logboek en in de Inbox.
        </p>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
